/**
 * Klub Pingwina landing — Framer Anchor Bar, badges, gallery, place tabs, FAQ.
 * Sticky section nav: pure CSS sticky (`.js-klub-anchor`) — not OfferSubNav.
 * Reviews slider: Home.js via `.js-reviews-slider`.
 * FAQ open/close animation: global faqAccordion.js (`.faq-item`).
 */

const getAnchorId = (link) => {
  const el = link instanceof HTMLAnchorElement ? link : link.querySelector('a');
  const href = (el || link).getAttribute('href') || '';
  if (!href.startsWith('#') || href === '#') return '';
  return href.slice(1);
};

const setAnchorActive = (links, sectionId) => {
  links.forEach((link) => {
    const on = Boolean(sectionId) && getAnchorId(link) === sectionId;
    link.classList.toggle('is-active', on);
    if (on) link.setAttribute('aria-current', 'true');
    else link.removeAttribute('aria-current');
  });
};

const anchorBarHeight = (nav) => {
  if (!(nav instanceof HTMLElement)) return 51;
  return Math.round(nav.getBoundingClientRect().height) || 51;
};

const stickyChromeOffset = () => {
  const raw = getComputedStyle(document.body).getPropertyValue('--sticky-offset').trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const scrollToAnchorTarget = (el, nav, { smooth = true } = {}) => {
  if (!(el instanceof HTMLElement) || !el.isConnected) return;
  const offset = stickyChromeOffset() + anchorBarHeight(nav) + 16;
  const top = window.scrollY + el.getBoundingClientRect().top - offset;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({
    top: Math.max(0, top),
    behavior: smooth && !reduce ? 'smooth' : 'auto',
  });
};

/** Keep active tab visible in the horizontal scroller (mobile). */
const scrollTabIntoView = (nav, link) => {
  if (!(nav instanceof HTMLElement) || !(link instanceof HTMLElement)) return;
  const navRect = nav.getBoundingClientRect();
  const linkRect = link.getBoundingClientRect();
  const pad = 24;
  if (linkRect.left < navRect.left + pad) {
    nav.scrollBy({ left: linkRect.left - navRect.left - pad, behavior: 'smooth' });
  } else if (linkRect.right > navRect.right - pad) {
    nav.scrollBy({ left: linkRect.right - navRect.right + pad, behavior: 'smooth' });
  }
};

const initAnchorNav = (root) => {
  const nav = root.querySelector('.js-klub-anchor');
  if (!(nav instanceof HTMLElement)) return;

  const links = [...nav.querySelectorAll('.klub-pingwina__anchor-tab')];
  if (!links.length) return;

  const targets = new Map();
  links.forEach((link) => {
    const id = getAnchorId(link);
    if (!id) return;
    const el = document.getElementById(id);
    if (el instanceof HTMLElement) targets.set(id, el);
  });

  const syncHeightVar = () => {
    document.documentElement.style.setProperty(
      '--sub-nav-height',
      `${anchorBarHeight(nav)}px`,
    );
  };
  syncHeightVar();

  let lockId = '';
  let lockTimer = 0;

  const pickActive = () => {
    if (lockId) return lockId;
    const focusY = stickyChromeOffset() + anchorBarHeight(nav) + 4;
    let chosen = '';
    targets.forEach((el, id) => {
      if (el.getBoundingClientRect().top <= focusY) chosen = id;
    });
    if (chosen) return chosen;
    return targets.keys().next().value || '';
  };

  const syncActive = () => {
    const id = pickActive();
    setAnchorActive(links, id);
    const activeLink = links.find((l) => getAnchorId(l) === id);
    if (activeLink) scrollTabIntoView(nav, activeLink);
  };

  nav.addEventListener('click', (event) => {
    const link = event.target.closest?.('.klub-pingwina__anchor-tab');
    if (!(link instanceof HTMLElement) || !nav.contains(link)) return;
    const id = getAnchorId(link);
    const el = id ? targets.get(id) || document.getElementById(id) : null;
    if (!(el instanceof HTMLElement)) return;
    event.preventDefault();
    lockId = id;
    setAnchorActive(links, id);
    scrollTabIntoView(nav, link);
    scrollToAnchorTarget(el, nav);
    history.replaceState(null, '', `#${id}`);
    window.clearTimeout(lockTimer);
    lockTimer = window.setTimeout(() => {
      lockId = '';
      syncActive();
    }, 800);
  });

  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      syncActive();
      ticking = false;
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      syncHeightVar();
      syncActive();
    },
    { passive: true },
  );

  const hashId = (window.location.hash || '').replace(/^#/, '');
  if (hashId && targets.has(hashId)) {
    lockId = hashId;
    setAnchorActive(links, hashId);
    requestAnimationFrame(() => {
      const el = targets.get(hashId);
      if (el) scrollToAnchorTarget(el, nav, { smooth: false });
      window.setTimeout(() => {
        lockId = '';
        syncActive();
      }, 100);
    });
  } else {
    syncActive();
  }
};

const BADGE_TAB_SELECTOR = '.js-school-badge-tab';
const BADGE_PANE_SELECTOR = '.js-school-badge-pane';

const activateBadge = (root, badgeId) => {
  if (!badgeId) return;
  root.querySelectorAll(BADGE_TAB_SELECTOR).forEach((btn) => {
    const active = btn.getAttribute('data-badge-id') === badgeId;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    if (btn.getAttribute('role') === 'tab') {
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    }
  });
  root.querySelectorAll(BADGE_PANE_SELECTOR).forEach((pane) => {
    const active = pane.getAttribute('data-badge-id') === badgeId;
    pane.classList.toggle('is-active', active);
    if (active) pane.removeAttribute('hidden');
    else pane.setAttribute('hidden', '');
  });
};

const badgeIds = (root) =>
  [...root.querySelectorAll(BADGE_PANE_SELECTOR)]
    .map((pane) => pane.getAttribute('data-badge-id'))
    .filter(Boolean);

const badgeNavButton = (className, label, iconFirst) => {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `school-product-page__badge-nav-btn ${className}`;
  btn.setAttribute('aria-label', label);
  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('width', '20');
  icon.setAttribute('height', '20');
  icon.setAttribute('viewBox', '0 0 20 20');
  icon.setAttribute('fill', 'none');
  icon.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute(
    'd',
    iconFirst
      ? 'M15.83 10H4.17M8.33 5.83 4.17 10l4.16 4.17'
      : 'M4.17 10h11.66M11.67 5.83 15.83 10l-4.16 4.17',
  );
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.5');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  icon.appendChild(path);
  const text = document.createElement('span');
  text.className = 'school-product-page__badge-nav-label';
  text.textContent = label;
  if (iconFirst) {
    btn.append(icon, text);
  } else {
    btn.append(text, icon);
  }
  return btn;
};

/** Wspólne media poziomu (Figma) — gdy slot Gutenberg nie ma .badge-media. */
const ensureBadgeMedia = (badges) => {
  if (badges.querySelector('.school-product-page__badge-media')) return;
  const src = badges.getAttribute('data-badge-media')?.trim();
  if (!src) return;
  const detail = badges.querySelector('.school-product-page__badge-detail');
  if (!(detail instanceof HTMLElement)) return;

  const wrap = document.createElement('div');
  wrap.className = 'school-product-page__badge-media';
  const image = document.createElement('img');
  image.src = src;
  image.alt = 'Zajęcia Klubu Pingwina na stoku';
  image.loading = 'lazy';
  image.decoding = 'async';
  wrap.appendChild(image);

  const nav = detail.querySelector('.school-product-page__badge-nav');
  if (nav) detail.insertBefore(wrap, nav);
  else detail.appendChild(wrap);
};

/** Legacy klub-only badge classes → school-product UI (saved slots / old editor markup). */
const LEGACY_KLUB_BADGE_CLASS_MAP = {
  'klub-pingwina__badge-pane-title': 'school-product-page__split-title',
  'klub-pingwina__badge-pane-head': 'school-product-page__badge-pane-head',
  'klub-pingwina__badge-tab-inner': 'school-product-page__badge-tab-inner',
  'klub-pingwina__badge-tab-label': 'school-product-page__badge-tab-label',
  'klub-pingwina__badge-tab-pin': 'school-product-page__badge-tab-pin',
  'klub-pingwina__badge-nav-label': 'school-product-page__badge-nav-label',
  'klub-pingwina__badge-nav-btn': 'school-product-page__badge-nav-btn',
  'klub-pingwina__badge-icon-btn': 'school-product-page__badge-icon-btn',
  'klub-pingwina__badge-panel': 'school-product-page__badge-panel',
  'klub-pingwina__badge-icons': 'school-product-page__badge-icons',
  'klub-pingwina__badge-tabs': 'school-product-page__badge-tabs',
  'klub-pingwina__badge-detail': 'school-product-page__badge-detail',
  'klub-pingwina__badge-list': 'school-product-page__badge-list',
  'klub-pingwina__badge-pane': 'school-product-page__badge-pane',
  'klub-pingwina__badge-nav': 'school-product-page__badge-nav',
  'klub-pingwina__badge-tab': 'school-product-page__badge-tab',
  'klub-pingwina__badge-media': 'school-product-page__badge-media',
  'js-klub-badge-pane': 'js-school-badge-pane',
  'js-klub-badge-prev': 'js-school-badge-prev',
  'js-klub-badge-next': 'js-school-badge-next',
  'js-klub-badge-tab': 'js-school-badge-tab',
};

const remapLegacyKlubBadgeClasses = (badges) => {
  badges.querySelectorAll('[class]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const next = [...el.classList].map((token) => LEGACY_KLUB_BADGE_CLASS_MAP[token] || token);
    const className = next.join(' ');
    if (className !== el.className) el.className = className;
  });
};

const badgeIdFromPin = (src, index) => {
  const match = String(src || '').match(/przypinka-p(\d+)/i);
  return match ? `p${match[1]}` : `p${index + 1}`;
};

/** Gutenberg slot ma same panele — dokładamy kółka, listę i „Kolejny” jak w prototypie. */
const ensureBadgePaneHead = (pane) => {
  if (!(pane instanceof HTMLElement)) return;
  if (pane.querySelector('.school-product-page__badge-pane-head')) return;

  const head = document.createElement('div');
  head.className = 'school-product-page__badge-pane-head';
  const textWrap = document.createElement('div');

  [...pane.childNodes].forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      node.remove();
      return;
    }
    if (
      node.classList.contains('school-product-page__badge-media')
      || node.classList.contains('school-product-page__badge-nav')
    ) {
      return;
    }
    node.remove();
    if (node.tagName === 'IMG' || node.classList.contains('school-product-page__badge-tab-pin')) {
      head.appendChild(node);
      return;
    }
    if (
      node.tagName === 'H3'
      || node.classList.contains('school-product-page__split-title')
      || node.classList.contains('school-product-page__badge-list')
      || node.tagName === 'OL'
    ) {
      if (node.tagName === 'H3' && !node.classList.contains('school-product-page__split-title')) {
        node.classList.add('school-product-page__split-title');
      }
      if (node.tagName === 'OL' && !node.classList.contains('school-product-page__badge-list')) {
        node.classList.add('school-product-page__badge-list');
      }
      textWrap.appendChild(node);
    }
  });

  if (textWrap.childNodes.length) head.appendChild(textWrap);
  if (head.childNodes.length) pane.prepend(head);
};

const ensureBadgeChrome = (badges) => {
  remapLegacyKlubBadgeClasses(badges);
  if (badges.querySelector('.js-school-badge-tab') && badges.querySelector('.school-product-page__badge-icons')) {
    return;
  }

  const panes = [...badges.querySelectorAll('.js-school-badge-pane')];
  if (panes.length < 2) return;

  panes.forEach((pane) => ensureBadgePaneHead(pane));

  const usedIds = new Set();
  panes.forEach((pane, index) => {
    const src = pane.querySelector('img')?.getAttribute('src') || '';
    let id = pane.getAttribute('data-badge-id') || badgeIdFromPin(src, index);
    if (usedIds.has(id)) id = `p${index + 1}`;
    usedIds.add(id);
    pane.setAttribute('data-badge-id', id);
    if (index === 0 && !panes.some((item) => item.classList.contains('is-active'))) {
      pane.classList.add('is-active');
    }
  });

  const icons = document.createElement('ul');
  icons.className = 'school-product-page__badge-icons';
  icons.setAttribute('aria-label', 'Odznaki');

  const tabs = document.createElement('div');
  tabs.className = 'school-product-page__badge-tabs';
  tabs.setAttribute('role', 'tablist');
  tabs.setAttribute('aria-label', 'Poziomy');

  panes.forEach((pane, index) => {
    const id = pane.getAttribute('data-badge-id');
    const title = pane.querySelector('.school-product-page__split-title')?.textContent?.trim()
      || `Poziom ${index + 1}`;
    const short = title.split('—')[0].trim() || title;
    const src = pane.querySelector('img')?.getAttribute('src') || '';
    const active = pane.classList.contains('is-active');

    const li = document.createElement('li');
    const iconBtn = document.createElement('button');
    iconBtn.type = 'button';
    iconBtn.className = `school-product-page__badge-icon-btn js-school-badge-tab${active ? ' is-active' : ''}`;
    iconBtn.setAttribute('data-badge-id', id);
    iconBtn.setAttribute('aria-pressed', active ? 'true' : 'false');
    if (src) {
      const image = document.createElement('img');
      image.src = src;
      image.alt = short;
      image.width = 119;
      image.height = 119;
      iconBtn.appendChild(image);
    } else {
      iconBtn.textContent = String(index + 1);
    }
    li.appendChild(iconBtn);
    icons.appendChild(li);

    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = `school-product-page__badge-tab js-school-badge-tab${active ? ' is-active' : ''}`;
    tab.setAttribute('data-badge-id', id);
    tab.setAttribute('role', 'tab');
    tab.setAttribute('aria-selected', active ? 'true' : 'false');
    tab.setAttribute('aria-pressed', active ? 'true' : 'false');
    const inner = document.createElement('span');
    inner.className = 'school-product-page__badge-tab-inner';
    if (src) {
      const pin = document.createElement('img');
      pin.className = 'school-product-page__badge-tab-pin';
      pin.src = src;
      pin.alt = '';
      pin.width = 90;
      pin.height = 90;
      inner.appendChild(pin);
    }
    const label = document.createElement('span');
    label.className = 'school-product-page__badge-tab-label';
    label.textContent = short;
    inner.appendChild(label);
    tab.appendChild(inner);
    tabs.appendChild(tab);
  });

  const panel = badges.querySelector('.school-product-page__badge-panel') || panes[0].parentElement;
  if (!(panel instanceof HTMLElement)) return;

  const detail = document.createElement('div');
  detail.className = 'school-product-page__badge-detail';
  const nav = document.createElement('div');
  nav.className = 'school-product-page__badge-nav';
  nav.append(
    badgeNavButton('js-school-badge-prev', 'Poprzedni', true),
    badgeNavButton('js-school-badge-next', 'Kolejny', false),
  );
  detail.append(...panes, nav);

  panel.replaceChildren(tabs, detail);

  const host = badges.querySelector('.klub-pingwina__badges-body') || panel.parentElement;
  if (host instanceof HTMLElement) host.insertBefore(icons, panel);

  const active = panes.find((pane) => pane.classList.contains('is-active')) || panes[0];
  activateBadge(badges, active.getAttribute('data-badge-id'));
};

/** Figma 1:636 — avatar colors + masonry column order by author. */
const REVIEW_FIGMA = {
  'Lilianna Leontiew-Bogiel': { initial: 'L', bg: '#f4511e', col: 0 },
  'Rafał Klamrzyński': { initial: 'R', bg: '#11ba63', col: 0 },
  'David Majerczyk': { initial: 'D', bg: '#8d6e63', col: 1 },
  'Małgorzata si': { initial: 'M', bg: '#f51d9f', col: 2 },
  'Seba C': { initial: 'S', bg: '#3a1df5', col: 2 },
};

/**
 * Gutenberg slot = flat name/date/stars/body — wrap into Figma card chrome
 * (avatar + meta + Google mark) and re-bucket columns to match 1:636.
 */
const ensureReviewsStructure = (root) => {
  const section = root.querySelector('.klub-pingwina__reviews');
  if (!(section instanceof HTMLElement)) return;

  const grid = section.querySelector('.klub-pingwina__reviews-grid');
  if (!(grid instanceof HTMLElement)) return;

  const imagesBase = themeImagesBase(root);
  const googleSrc = `${imagesBase}/offer-card/google.png`;
  const starSrc = `${imagesBase}/icons/google-star.svg`;

  const cards = [...grid.querySelectorAll('.klub-pingwina__review')];
  cards.forEach((card) => {
    if (!(card instanceof HTMLElement)) return;

    const nameEl = card.querySelector('.klub-pingwina__review-name');
    const dateEl = card.querySelector('.klub-pingwina__review-date');
    const starsEl = card.querySelector('.klub-pingwina__review-stars');
    const author = nameEl?.textContent?.trim() || '';
    const meta = REVIEW_FIGMA[author] || {
      initial: author ? author.charAt(0).toUpperCase() : '?',
      bg: '#4475f2',
      col: 0,
    };

    if (!card.querySelector('.klub-pingwina__review-top') && nameEl) {
      const top = document.createElement('div');
      top.className = 'klub-pingwina__review-top';

      const user = document.createElement('div');
      user.className = 'klub-pingwina__review-user';

      const avatar = document.createElement('span');
      avatar.className = 'klub-pingwina__review-avatar';
      avatar.style.setProperty('--avatar-bg', meta.bg);
      avatar.textContent = meta.initial;

      const metaWrap = document.createElement('div');
      metaWrap.className = 'klub-pingwina__review-meta';
      metaWrap.appendChild(nameEl);
      if (dateEl instanceof HTMLElement) metaWrap.appendChild(dateEl);

      user.append(avatar, metaWrap);

      const google = document.createElement('img');
      google.className = 'klub-pingwina__review-google';
      google.src = googleSrc;
      google.alt = '';
      google.width = 24;
      google.height = 24;
      google.loading = 'lazy';
      google.decoding = 'async';

      top.append(user, google);
      card.insertBefore(top, card.firstChild);
    } else {
      const avatar = card.querySelector('.klub-pingwina__review-avatar');
      if (avatar instanceof HTMLElement) {
        avatar.style.setProperty('--avatar-bg', meta.bg);
        if (!avatar.textContent?.trim()) avatar.textContent = meta.initial;
      }
      const google = card.querySelector('.klub-pingwina__review-google');
      if (google instanceof HTMLImageElement) {
        google.width = 24;
        google.height = 24;
      }
    }

    if (starsEl instanceof HTMLElement && !starsEl.querySelector('img')) {
      const score = Math.min(5, Math.max(1, (starsEl.textContent || '★★★★★').replace(/[^★]/g, '').length || 5));
      starsEl.textContent = '';
      starsEl.setAttribute('aria-label', `${score} gwiazdek`);
      for (let i = 0; i < 5; i += 1) {
        const img = document.createElement('img');
        img.src = starSrc;
        img.alt = '';
        img.width = 24;
        img.height = 24;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.setAttribute('aria-hidden', 'true');
        if (i >= score) img.style.opacity = '0.25';
        starsEl.appendChild(img);
      }
    }
  });

  // Re-bucket into Figma columns when all five known authors are present.
  const known = cards.filter((card) => {
    const name = card.querySelector('.klub-pingwina__review-name')?.textContent?.trim() || '';
    return Boolean(REVIEW_FIGMA[name]);
  });
  if (known.length === 5) {
    let cols = [...grid.querySelectorAll(':scope > .klub-pingwina__reviews-col')];
    while (cols.length < 3) {
      const col = document.createElement('div');
      col.className = 'klub-pingwina__reviews-col';
      grid.appendChild(col);
      cols = [...grid.querySelectorAll(':scope > .klub-pingwina__reviews-col')];
    }
    const buckets = [[], [], []];
    const order = [
      'Lilianna Leontiew-Bogiel',
      'Rafał Klamrzyński',
      'David Majerczyk',
      'Małgorzata si',
      'Seba C',
    ];
    order.forEach((name) => {
      const card = known.find(
        (el) => el.querySelector('.klub-pingwina__review-name')?.textContent?.trim() === name,
      );
      if (!card) return;
      const colIdx = REVIEW_FIGMA[name].col;
      buckets[colIdx].push(card);
    });
    buckets.forEach((list, idx) => {
      const col = cols[idx];
      if (!(col instanceof HTMLElement)) return;
      list.forEach((card) => col.appendChild(card));
    });
    cols.slice(3).forEach((col) => {
      if (col instanceof HTMLElement && !col.querySelector('.klub-pingwina__review')) col.remove();
    });
  }

  const btn = section.querySelector('.klub-pingwina__reviews-btn');
  if (btn instanceof HTMLElement) {
    const label = (btn.textContent || '').trim();
    if (label === 'Google Opinie o HSKI') {
      btn.textContent = 'Google opinie o HSKI';
    }
  }
};

/**
 * Gutenberg slot dumps flat title/date/text/amount/unit/btn — wrap into
 * Framer clusters: copy | (price + btn) | phone.
 */
const ensurePriceCtaStructure = (root) => {
  root.querySelectorAll('.klub-pingwina__price-cta-bar').forEach((bar) => {
    if (!(bar instanceof HTMLElement)) return;
    if (bar.querySelector('.klub-pingwina__price-cta-copy')) return;

    const title = bar.querySelector('.klub-pingwina__price-cta-title');
    const date = bar.querySelector('.klub-pingwina__price-cta-date');
    const text = bar.querySelector('.klub-pingwina__price-cta-text');
    const amount = bar.querySelector('.klub-pingwina__price-cta-amount');
    const unit = bar.querySelector('.klub-pingwina__price-cta-unit');
    let actions = bar.querySelector('.klub-pingwina__price-cta-actions');
    const phone = bar.querySelector('.klub-pingwina__price-cta-phone');
    const btn = bar.querySelector('.klub-pingwina__price-cta-btn');

    if (!title && !amount && !actions && !btn) return;

    if (title instanceof HTMLElement && date instanceof HTMLElement && !title.contains(date)) {
      const dateSpan = document.createElement('span');
      dateSpan.className = 'klub-pingwina__price-cta-date';
      dateSpan.textContent = date.textContent?.trim() || '';
      title.appendChild(document.createTextNode(' '));
      title.appendChild(dateSpan);
      date.remove();
    }

    const copy = document.createElement('div');
    copy.className = 'klub-pingwina__price-cta-copy';
    if (title instanceof HTMLElement) copy.appendChild(title);
    if (text instanceof HTMLElement) copy.appendChild(text);

    if (!(actions instanceof HTMLElement)) {
      actions = document.createElement('div');
      actions.className = 'klub-pingwina__price-cta-actions';
      if (btn instanceof HTMLElement) actions.appendChild(btn);
    }

    if ((amount || unit) && !actions.querySelector('.klub-pingwina__price-cta-price')) {
      const price = document.createElement('div');
      price.className = 'klub-pingwina__price-cta-price';
      if (amount instanceof HTMLElement) price.appendChild(amount);
      if (unit instanceof HTMLElement) price.appendChild(unit);
      actions.insertBefore(price, actions.firstChild);
    }

    const ordered = [copy, actions];
    if (phone instanceof HTMLElement) ordered.push(phone);

    ordered.forEach((node) => {
      if (node.parentElement !== bar) bar.appendChild(node);
    });

    // Drop leftover empty wrappers / whitespace-only nodes from the slot.
    [...bar.children].forEach((child) => {
      if (ordered.includes(child)) return;
      if (child instanceof HTMLElement && child.matches('[data-hski-slot-extra]')) return;
      if (child.textContent?.trim() || child.querySelector?.('img,svg,a,button')) return;
      child.remove();
    });

    // Ensure visual order: copy → actions → phone (phone stays last).
    ordered.forEach((node) => bar.appendChild(node));
  });
};

const scrollBadgeTabIntoRow = (wrap, rowSel, tabSel) => {
  const row = wrap.querySelector(rowSel);
  const active = wrap.querySelector(tabSel);
  if (!active || !row || !row.contains(active) || row.scrollWidth <= row.clientWidth) return;
  const rowBox = row.getBoundingClientRect();
  const tabBox = active.getBoundingClientRect();
  const pad = 16;
  if (tabBox.left < rowBox.left + pad) {
    row.scrollBy({ left: tabBox.left - rowBox.left - pad, behavior: 'smooth' });
  } else if (tabBox.right > rowBox.right - pad) {
    row.scrollBy({ left: tabBox.right - rowBox.right + pad, behavior: 'smooth' });
  }
};

const initBadges = (root) => {
  const badges = root.querySelector('.js-school-badges');
  if (!(badges instanceof HTMLElement)) return;

  remapLegacyKlubBadgeClasses(badges);
  if (!badges.querySelector('.js-school-badge-tab') || !badges.querySelector('.school-product-page__badge-icons')) {
    ensureBadgeChrome(badges);
  }
  ensureBadgeMedia(badges);

  badges.addEventListener('click', (event) => {
    const tab = event.target.closest?.(BADGE_TAB_SELECTOR);
    if (!(tab instanceof HTMLElement) || !badges.contains(tab)) return;
    event.preventDefault();
    const id = tab.getAttribute('data-badge-id');
    activateBadge(badges, id);
    scrollBadgeTabIntoRow(
      badges,
      '.school-product-page__badge-icons',
      '.school-product-page__badge-icons .js-school-badge-tab.is-active',
    );
    scrollBadgeTabIntoRow(
      badges,
      '.school-product-page__badge-tabs',
      '.school-product-page__badge-tabs .js-school-badge-tab.is-active',
    );
  });

  const ids = badgeIds(badges);
  const activeBadgeId = () =>
    [...badges.querySelectorAll(BADGE_PANE_SELECTOR)].find((pane) => pane.classList.contains('is-active'))
      ?.getAttribute('data-badge-id') || ids[0];
  const prevBtn = badges.querySelector('.js-school-badge-prev');
  const nextBtn = badges.querySelector('.js-school-badge-next');
  const syncNavDisabled = (id) => {
    const index = ids.indexOf(id);
    if (prevBtn instanceof HTMLButtonElement) prevBtn.disabled = index <= 0;
    if (nextBtn instanceof HTMLButtonElement) nextBtn.disabled = index < 0 || index >= ids.length - 1;
  };
  syncNavDisabled(activeBadgeId());

  prevBtn?.addEventListener('click', () => {
    const idx = ids.indexOf(activeBadgeId());
    const nextId = ids[(idx - 1 + ids.length) % ids.length];
    activateBadge(badges, nextId);
    syncNavDisabled(nextId);
  });
  nextBtn?.addEventListener('click', () => {
    const idx = ids.indexOf(activeBadgeId());
    const nextId = ids[(idx + 1) % ids.length];
    activateBadge(badges, nextId);
    syncNavDisabled(nextId);
  });
};

const galleryItemType = (item) => {
  const attr = item.getAttribute('data-gallery-type');
  if (attr) return attr;
  if (item.classList.contains('klub-pingwina__gallery-item--video')) return 'video';
  const tag = item.querySelector('.klub-pingwina__gallery-tag')?.textContent?.trim() || '';
  if (/^FILM\b/i.test(tag)) return 'video';
  return 'photo';
};

/** Gutenberg slot uses figure/img groups without <a> — PhotoSwipe needs anchors. */
const ensureGalleryLightboxLinks = (gallery) => {
  gallery.querySelectorAll('.klub-pingwina__gallery-item').forEach((item) => {
    if (!(item instanceof HTMLElement)) return;
    if (galleryItemType(item) === 'video') return;
    if (item.matches('a[href]')) return;
    if (item.querySelector('a[href]')) return;
    const img = item.querySelector('img');
    if (!(img instanceof HTMLImageElement) || !img.src) return;
    const wrapTarget = img.closest('figure') || img;
    if (wrapTarget.parentElement?.closest('a[href]')) return;

    const link = document.createElement('a');
    link.href = img.currentSrc || img.src;
    link.className = 'klub-pingwina__gallery-link';
    const w = img.naturalWidth || Number(img.getAttribute('width')) || 1200;
    const h = img.naturalHeight || Number(img.getAttribute('height')) || 800;
    link.setAttribute('data-pswp-width', String(w));
    link.setAttribute('data-pswp-height', String(h));
    wrapTarget.parentNode?.insertBefore(link, wrapTarget);
    link.appendChild(wrapTarget);
  });
};

const syncGalleryColumns = (gallery) => {
  gallery.querySelectorAll('.klub-pingwina__gallery-col').forEach((col) => {
    if (!(col instanceof HTMLElement)) return;
    const visible = [...col.querySelectorAll('.klub-pingwina__gallery-item')].some(
      (item) => !item.classList.contains('is-hidden') && !item.hidden,
    );
    col.classList.toggle('is-hidden', !visible);
    col.hidden = !visible;
  });
};

const initGalleryFilters = (root) => {
  const gallery = root.querySelector('.js-klub-gallery');
  if (!(gallery instanceof HTMLElement)) return;

  const filters = gallery.querySelectorAll('.js-klub-gallery-filter');
  const items = [...gallery.querySelectorAll('.klub-pingwina__gallery-item')];

  items.forEach((item) => {
    if (!item.getAttribute('data-gallery-type')) {
      item.setAttribute('data-gallery-type', galleryItemType(item));
    }
  });

  ensureGalleryLightboxLinks(gallery);

  filters.forEach((btn) => {
    btn.addEventListener('click', () => {
      const filter = btn.getAttribute('data-filter') || 'all';
      filters.forEach((el) => {
        const on = el === btn;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      items.forEach((item) => {
        const type = galleryItemType(item);
        const show = filter === 'all' || filter === type;
        item.hidden = !show;
        item.classList.toggle('is-hidden', !show);
      });
      syncGalleryColumns(gallery);
    });
  });
};

const placeArrowSvg = (dir) => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '12');
  svg.setAttribute('height', '12');
  svg.setAttribute('viewBox', '0 0 12 12');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute(
    'd',
    dir === 'prev' ? 'M7.5 2.5 4 6l3.5 3.5' : 'M4.5 2.5 8 6l-3.5 3.5',
  );
  path.setAttribute('stroke', '#ff5000');
  path.setAttribute('stroke-width', '1.6');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  svg.appendChild(path);
  return svg;
};

/** Gutenberg często ma same figure.slide bez wrappera / kropek — dobuduj UI jak Twig. */
const enhancePlaceMedia = (root) => {
  root.querySelectorAll('.klub-pingwina__place-media').forEach((media) => {
    if (!(media instanceof HTMLElement)) return;

    let slidesWrap = media.querySelector(':scope > .klub-pingwina__place-slides');
    let slides = slidesWrap
      ? [...slidesWrap.querySelectorAll(':scope > .klub-pingwina__place-slide')]
      : [...media.children].filter(
          (el) => el instanceof HTMLElement && el.classList.contains('klub-pingwina__place-slide'),
        );

    if (slides.length === 0) return;

    if (!slidesWrap) {
      slidesWrap = document.createElement('div');
      slidesWrap.className = 'klub-pingwina__place-slides';
      slides.forEach((slide) => slidesWrap.appendChild(slide));
      media.insertBefore(slidesWrap, media.firstChild);
    }

    if (!slides.some((slide) => slide.classList.contains('is-active'))) {
      slides[0].classList.add('is-active');
    }

    media.classList.add('js-klub-place-slider');

    if (slides.length < 2 || media.querySelector('.klub-pingwina__place-controls')) return;

    const controls = document.createElement('div');
    controls.className = 'klub-pingwina__place-controls';

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'klub-pingwina__place-arrow js-klub-place-prev';
    prev.setAttribute('aria-label', 'Poprzedni slajd');
    prev.appendChild(placeArrowSvg('prev'));

    const dots = document.createElement('div');
    dots.className = 'klub-pingwina__place-dots';
    dots.setAttribute('role', 'tablist');
    dots.setAttribute('aria-label', 'Slajdy lokalizacji');
    slides.forEach((slide, index) => {
      const on = slide.classList.contains('is-active');
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'klub-pingwina__place-dot js-klub-place-dot' + (on ? ' is-active' : '');
      dot.setAttribute('aria-label', `Slajd ${index + 1}`);
      dot.setAttribute('aria-selected', on ? 'true' : 'false');
      dots.appendChild(dot);
    });

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'klub-pingwina__place-arrow js-klub-place-next';
    next.setAttribute('aria-label', 'Następny slajd');
    next.appendChild(placeArrowSvg('next'));

    controls.append(prev, dots, next);
    media.appendChild(controls);
  });
};

const initPlaceTabs = (root) => {
  const place = root.querySelector('.js-klub-place');
  if (!(place instanceof HTMLElement)) return;

  const tabs = [...place.querySelectorAll('.js-klub-place-tab')];
  const panes = [...place.querySelectorAll('.js-klub-place-pane')];

  tabs.forEach((tab, index) => {
    const id = tab.getAttribute('data-place');
    const pane = panes[index];
    if (id && pane instanceof HTMLElement && !pane.getAttribute('data-place')) {
      pane.setAttribute('data-place', id);
    }
  });

  const hasActivePane = panes.some((p) => p.classList.contains('is-active'));
  panes.forEach((pane, index) => {
    if (!(pane instanceof HTMLElement)) return;
    const on = hasActivePane ? pane.classList.contains('is-active') : index === 0;
    pane.classList.toggle('is-active', on);
    if (on) pane.removeAttribute('hidden');
    else pane.setAttribute('hidden', '');
  });

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const id = tab.getAttribute('data-place');
      tabs.forEach((el) => {
        const on = el === tab;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      const tabIndex = tabs.findIndex((el) => el.getAttribute('data-place') === id);
      panes.forEach((pane, index) => {
        const paneId = pane.getAttribute('data-place');
        const on = paneId ? paneId === id : index === tabIndex;
        pane.classList.toggle('is-active', on);
        if (on) pane.removeAttribute('hidden');
        else pane.setAttribute('hidden', '');
      });
    });
  });
};

const initPlaceSlider = (root) => {
  enhancePlaceMedia(root);

  const sliders = root.querySelectorAll('.js-klub-place-slider');
  sliders.forEach((slider) => {
    if (!(slider instanceof HTMLElement)) return;
    const slides = [...slider.querySelectorAll('.klub-pingwina__place-slide')];
    const dots = [...slider.querySelectorAll('.js-klub-place-dot')];
    const prev = slider.querySelector('.js-klub-place-prev');
    const next = slider.querySelector('.js-klub-place-next');
    if (slides.length < 2) return;

    let current = Math.max(
      0,
      slides.findIndex((slide) => slide.classList.contains('is-active')),
    );

    const goTo = (index) => {
      const i = ((index % slides.length) + slides.length) % slides.length;
      current = i;
      slides.forEach((slide, n) => slide.classList.toggle('is-active', n === i));
      dots.forEach((dot, n) => {
        const on = n === i;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    };

    dots.forEach((dot, n) => {
      dot.addEventListener('click', () => goTo(n));
    });

    if (prev instanceof HTMLElement) {
      prev.addEventListener('click', () => goTo(current - 1));
    }
    if (next instanceof HTMLElement) {
      next.addEventListener('click', () => goTo(current + 1));
    }
  });
};

// Na tablecie/mobile tytuł w jednej linii. <br> z treści zostają na desktop (>1024).
const initHeroTitleBreaks = (root) => {
  const title = root.querySelector('.klub-pingwina__hero-title');
  if (!(title instanceof HTMLElement)) return;
  const original = title.innerHTML;
  const flat = original.replace(/<br\s*\/?>/gi, ' ');
  const narrow = window.matchMedia('(max-width: 1024px)');
  const apply = () => {
    const next = narrow.matches ? flat : original;
    if (title.innerHTML !== next) title.innerHTML = next;
  };
  apply();
  narrow.addEventListener('change', apply);
};

const initHeroVideo = (root) => {
  const hero = root.querySelector('.klub-pingwina__hero');
  const video = root.querySelector('.js-klub-hero-video');
  if (!(hero instanceof HTMLElement) || !(video instanceof HTMLVideoElement)) return;

  const canPlayVideo = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return window.matchMedia('(min-width: 768px)').matches;
  };

  const sync = () => {
    if (!canPlayVideo()) {
      video.pause();
      hero.classList.remove('is-video-ready');
      return;
    }
    const play = video.play();
    if (play && typeof play.then === 'function') {
      play
        .then(() => hero.classList.add('is-video-ready'))
        .catch(() => hero.classList.remove('is-video-ready'));
    }
  };

  video.addEventListener('playing', () => hero.classList.add('is-video-ready'));
  window.addEventListener('resize', sync, { passive: true });
  sync();
};

const themeImagesBase = (root) => {
  const probe = root.querySelector('img[src*="/themes/HSKI/assets/images/"]');
  const src = probe?.getAttribute('src') || '';
  const match = src.match(/^(.*\/themes\/HSKI\/assets\/images)\//);
  return match?.[1] || '/wp-content/themes/HSKI/assets/images';
};

/** Gutenberg slot: tylko pierwszy filar ma label PZN — dokładamy ikony SVG jak w prototypie. */
const ensurePillarIcons = (root) => {
  const imagesBase = themeImagesBase(root);
  const specs = [
    null,
    `${imagesBase}/klub-pingwina/pillars/groups.svg`,
    `${imagesBase}/klub-pingwina/pillars/trophy.svg`,
    `${imagesBase}/klub-pingwina/pillars/ski.svg`,
  ];

  root.querySelectorAll('.klub-pingwina__pillars > .klub-pingwina__pillar').forEach((pillar, index) => {
    if (!(pillar instanceof HTMLElement)) return;
    if (pillar.querySelector('.klub-pingwina__pillar-icon img')) return;
    const src = specs[index];
    if (!src) return;

    const title = pillar.querySelector('.klub-pingwina__pillar-title');
    if (!(title instanceof HTMLElement)) return;

    const wrap = document.createElement('div');
    wrap.className = 'klub-pingwina__pillar-icon';
    wrap.setAttribute('aria-hidden', 'true');
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.width = 28;
    img.height = 29;
    img.loading = 'lazy';
    img.decoding = 'async';
    wrap.appendChild(img);

    const head = pillar.querySelector('.klub-pingwina__pillar-head');
    if (head instanceof HTMLElement) {
      head.insertBefore(wrap, head.firstChild);
      return;
    }

    title.parentElement?.insertBefore(wrap, title);
  });
};

const appendDayDeco = (host, imagesBase, { blueClass, orangeClass, blueW, blueH, orangeW, orangeH }) => {
  if (!(host instanceof HTMLElement) || host.querySelector(`.${blueClass.split(' ')[0]}`)) return;
  const blue = document.createElement('img');
  blue.className = blueClass;
  blue.src = `${imagesBase}/home/contact-triangle-blue-lg.svg`;
  blue.alt = '';
  blue.width = blueW;
  blue.height = blueH;
  blue.loading = 'lazy';
  blue.setAttribute('aria-hidden', 'true');
  const orange = document.createElement('img');
  orange.className = orangeClass;
  orange.src = `${imagesBase}/school/deco-triangle-orange.svg`;
  orange.alt = '';
  orange.width = orangeW;
  orange.height = orangeH;
  orange.loading = 'lazy';
  orange.setAttribute('aria-hidden', 'true');
  host.append(blue, orange);
};

const enhanceDayMarkup = (root) => {
  const imagesBase = themeImagesBase(root);

  root.querySelectorAll('.klub-pingwina__timeline-time').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const raw = (el.textContent || '').trim();
    if (!raw) return;

    if (!/\d/.test(raw) || /^w\s+trakcie/i.test(raw)) {
      el.classList.add('klub-pingwina__timeline-time--label');
      return;
    }

    if (el.querySelector('span')) return;
    const parts = raw.split(/\s*[–—-]\s*/).filter(Boolean);
    if (parts.length < 2) return;
    el.textContent = '';
    parts.forEach((part) => {
      const span = document.createElement('span');
      span.textContent = part;
      el.append(span);
    });
  });

  root.querySelectorAll('.klub-pingwina__timeline-card--app, .klub-pingwina__app-title').forEach((node) => {
    const card = node.closest?.('.klub-pingwina__timeline-card--app, .js-klub-day-card');
    if (card instanceof HTMLElement && card.classList.contains('klub-pingwina__timeline-card--app')) {
      card.remove();
    }
  });
  root.querySelectorAll('.klub-pingwina__timeline-card--app').forEach((card) => {
    card.remove();
  });

  root.querySelectorAll('.klub-pingwina__timeline-body p').forEach((p) => {
    if (!(p instanceof HTMLElement)) return;
    if (!/aplikacji HSKI|HSKI app/i.test(p.textContent || '')) return;
    p.textContent = (p.textContent || '')
      .replace(/\s*[–—-]\s*możesz je też śledzić w aplikacji HSKI\.?/gi, '.')
      .replace(/\s*[–—-]\s*you can also (track them|follow their progress) in the HSKI app\.?/gi, '.')
      .replace(/\s{2,}/g, ' ')
      .trim();
  });

  root.querySelectorAll('.klub-pingwina__timeline-media').forEach((media) => {
    appendDayDeco(media, imagesBase, {
      blueClass: 'klub-pingwina__timeline-deco klub-pingwina__timeline-deco--blue',
      orangeClass: 'klub-pingwina__timeline-deco klub-pingwina__timeline-deco--orange',
      blueW: 80,
      blueH: 75,
      orangeW: 148,
      orangeH: 100,
    });
  });

};

/** Day cards: CSS sticky stack only — no scroll-driven translateY / opacity. */
const initDayStack = (root) => {
  enhanceDayMarkup(root);

  const pin = root.querySelector('.js-klub-day-pin');
  const stack = root.querySelector('.js-klub-day-stack');
  if (stack instanceof HTMLElement) {
    stack.style.height = '';
  }
  if (!(pin instanceof HTMLElement)) return;

  pin.style.height = '';
  pin.querySelectorAll('.js-klub-day-card').forEach((card) => {
    card.classList.remove('is-active', 'is-passed', 'is-upcoming', 'is-covering');
    card.style.transform = '';
    card.style.position = '';
    card.style.opacity = '';
    card.style.visibility = '';
    card.style.zIndex = '';
    card.removeAttribute('aria-hidden');
  });
};

const closeFaqItem = (item) => {
  if (!(item instanceof HTMLElement) || !item.open) return;
  const content = item.querySelector(':scope > .faq-item__content');
  if (!(content instanceof HTMLElement)) {
    item.removeAttribute('open');
    return;
  }
  if (item.classList.contains('is-animating')) return;
  item.classList.add('is-animating');
  content.style.gridTemplateRows = '0fr';
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    content.removeEventListener('transitionend', onEnd);
    item.removeAttribute('open');
    item.classList.remove('is-animating');
    content.style.gridTemplateRows = '';
  };
  const onEnd = (event) => {
    if (event.propertyName !== 'grid-template-rows') return;
    finish();
  };
  content.addEventListener('transitionend', onEnd);
  window.setTimeout(finish, 700);
};

const openFaqItem = (item) => {
  if (!(item instanceof HTMLElement) || item.open) return;
  const content = item.querySelector(':scope > .faq-item__content');
  item.setAttribute('open', '');
  if (!(content instanceof HTMLElement)) return;
  content.style.gridTemplateRows = '0fr';
  requestAnimationFrame(() => {
    content.style.gridTemplateRows = '1fr';
  });
};

/** Smooth one-open FAQ. Sets faqBound so the global accordion does not double-toggle. */
const initFaqExclusive = (root) => {
  const list = root.querySelector('.js-klub-faq');
  if (!(list instanceof HTMLElement)) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  list.querySelectorAll(':scope > .faq-item > summary').forEach((summary) => {
    if (!(summary instanceof HTMLElement) || summary.dataset.klubFaqBound === 'true') return;
    const owned = summary.dataset.faqBound !== 'true';
    summary.dataset.klubFaqBound = 'true';
    summary.dataset.faqBound = 'true';

    const item = summary.closest('.faq-item');
    const content = item instanceof HTMLElement
      ? item.querySelector(':scope > .faq-item__content')
      : null;
    if (item instanceof HTMLElement && item.open && content instanceof HTMLElement) {
      content.style.gridTemplateRows = '1fr';
    }

    summary.addEventListener('click', (event) => {
      if (!(item instanceof HTMLElement)) return;
      if (item.classList.contains('is-animating')) {
        event.preventDefault();
        return;
      }

      const closeOthers = () => {
        list.querySelectorAll(':scope > .faq-item[open]').forEach((other) => {
          if (other === item) return;
          if (reduce.matches) {
            other.removeAttribute('open');
            const otherContent = other.querySelector(':scope > .faq-item__content');
            if (otherContent instanceof HTMLElement) otherContent.style.gridTemplateRows = '';
            return;
          }
          closeFaqItem(other);
        });
      };

      if (!owned) {
        if (!item.open) closeOthers();
        return;
      }

      event.preventDefault();
      if (item.open) {
        if (reduce.matches) {
          item.removeAttribute('open');
          if (content instanceof HTMLElement) content.style.gridTemplateRows = '';
          return;
        }
        closeFaqItem(item);
        return;
      }

      closeOthers();
      if (reduce.matches) {
        item.setAttribute('open', '');
        if (content instanceof HTMLElement) content.style.gridTemplateRows = '1fr';
        return;
      }
      openFaqItem(item);
    });
  });
};

const initRacesVideo = (root) => {
  const video = root.querySelector('.js-klub-races-video');
  if (!(video instanceof HTMLVideoElement)) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const play = () => {
    if (reduceMotion.matches) {
      video.pause();
      return;
    }
    const p = video.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  if (!('IntersectionObserver' in window)) {
    play();
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) play();
        else video.pause();
      });
    },
    { threshold: 0.35 },
  );
  io.observe(video);
  reduceMotion.addEventListener?.('change', () => {
    if (reduceMotion.matches) video.pause();
    else play();
  });
};

/** Figma 1:1075 — niebieski akcent w H2 + rozbicie „48+” bez reseedu post_content. */
const initStaffAccent = (root) => {
  const title = root.querySelector('.klub-pingwina__staff-title');
  if (title instanceof HTMLElement && !title.querySelector('span, em, strong, code')) {
    const next = title.innerHTML.replace(/(instruktor[óo]w)/i, '<span>$1</span>');
    if (next !== title.innerHTML) title.innerHTML = next;
  }

  root.querySelectorAll('.klub-pingwina__staff-stat').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.querySelector('.klub-pingwina__staff-stat-plus')) return;
    const match = (el.textContent || '').trim().match(/^(\d+)\s*(\+)$/);
    if (!match) return;
    el.innerHTML = `<span>${match[1]}</span><span class="klub-pingwina__staff-stat-plus">${match[2]}</span>`;
  });
};

export default function initKlubPingwina() {
  const root = document.querySelector('.js-klub-pingwina');
  if (!(root instanceof HTMLElement)) return;
  initHeroTitleBreaks(root);
  initHeroVideo(root);
  initAnchorNav(root);
  ensurePillarIcons(root);
  initBadges(root);
  ensurePriceCtaStructure(root);
  ensureReviewsStructure(root);
  initGalleryFilters(root);
  initPlaceTabs(root);
  initPlaceSlider(root);
  initDayStack(root);
  initStaffAccent(root);
  initRacesVideo(root);
  initFaqExclusive(root);
}
